import { CKEditorContext } from '@ckeditor/ckeditor5-react';
import { QBox, useCurrentUser } from '@qualio/ui-components';
import { Context, DecoupledEditor, EventInfo } from 'ckeditor5';
import { Annotation } from 'ckeditor5-premium-features';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getIndependentContextConfig } from '../../../../config/DocumentEditorConfig';
import { setupEditorConnectionClosedHandler } from '../../../../config/handlers';
import {
  handleAddFileDocumentComment,
  setupDeletedByUserHandlers,
} from '../../../../config/handlers/CommentEventHandler';
import { useAnnotationHighlighting } from '../../../../hooks/AnnotationHighlighting';
import { useElementIsRendered } from '../../../../hooks/ElementRenderedCheck';
import { getHTMLElement } from '../../../../util/HTMLElementUtils';
import { refreshDisplayMode } from '../../../../util/SidebarUtils';
import { useDocumentOverviewContext } from '../../DocumentOverview/Context';
import { QualioAttachmentPreview } from '../hooks/useAttachmentPreview';

export const FileDocumentCommentsSidebar: React.FC<{
  attachmentPreview: QualioAttachmentPreview;
  handleSessionDisconnectionEvent?: (didReceive: boolean) => void;
}> = ({ attachmentPreview, handleSessionDisconnectionEvent }) => {
  const currentUser = useCurrentUser();
  const [searchParams] = useSearchParams();

  const { handleHighlighting } = useAnnotationHighlighting();
  const [isSidebarRendered, sidebarElement] =
    useElementIsRendered('#commentsSidebar');
  const {
    qualioDocument,
    collaborationToken,
    commentsPermission,
    users,
    tags,
  } = useDocumentOverviewContext();

  const targetId = searchParams.get('targetId') ?? undefined;

  const setupCommentButton = useCallback(
    (context: any) => {
      const channelId = context.config.get('collaboration').channelId;
      const commentsRepository = context.plugins.get('CommentsRepository');
      const commentButton = getHTMLElement(
        '#controls button[aria-label="Comment"]',
      );
      const { id } = qualioDocument;

      // Only register event listener if both document id and attachment id are present
      if (id && attachmentPreview.id) {
        commentButton?.addEventListener('click', () => {
          const commentsAnchor = getHTMLElement('#commentsAnchor');
          const threadId = `${id}-${attachmentPreview.id}-${Date.now()}`;
          commentsRepository.openNewCommentThread({
            channelId,
            threadId,
            target: commentsAnchor,
          });
        });
      }
      if (commentButton?.hasAttribute('disabled')) {
        commentButton?.removeAttribute('disabled');
      }
    },
    [attachmentPreview.id, qualioDocument],
  );

  const setupComments = useCallback(
    (context: any) => {
      const channelId = context.config.get('collaboration').channelId;
      const commentsRepository = context.plugins.get('CommentsRepository');

      for (const thread of commentsRepository.getCommentThreads({
        channelId,
      })) {
        handleCommentThread(thread);
      }

      commentsRepository.on(
        'addCommentThread:' + channelId,
        (_evt: any, data: any) => {
          handleCommentThread(
            commentsRepository.getCommentThread(data.threadId),
          );
        },
        { priority: 'low' },
      );

      commentsRepository?.on(
        'addComment',
        (evt: EventInfo, data: CommentsRepository.CommentData) => {
          if (attachmentPreview.filename) {
            handleAddFileDocumentComment(
              evt,
              data,
              currentUser,
              attachmentPreview.filename,
            );
          }
        },
        {
          priority: 'highest',
        },
      );

      function handleCommentThread(thread: any) {
        const commentsAnchor = getHTMLElement('#commentsAnchor');
        if (!thread.isAttached && !thread.deletedAt) {
          thread.attachTo(commentsAnchor);
        }
      }
    },
    [attachmentPreview.filename, currentUser],
  );

  const targetIdentifierPredicate = useCallback(
    (annotation: Annotation): boolean => {
      if (annotation.view.content.length > 0) {
        return (annotation.view.content as any)._items.some(
          (element: any) => element.commentThread.id === targetId,
        );
      } else {
        return false;
      }
    },
    [targetId],
  );

  const contextReadyHandler = useCallback(
    (context: Context) => {
      refreshDisplayMode({
        instances: [context],
        windowWidth: window.innerWidth,
        breakpointOverride: 1800,
      });
      window.addEventListener('resize', () => {
        refreshDisplayMode({
          instances: [context],
          windowWidth: window.innerWidth,
          breakpointOverride: 1800,
        });
      });
      setupComments(context);
      setupCommentButton(context);
      if (commentsPermission >= 2) {
        setupDeletedByUserHandlers(context, currentUser);
      }
      if (handleSessionDisconnectionEvent) {
        setupEditorConnectionClosedHandler(
          context.plugins.get('WebSocketGateway') as any,
          handleSessionDisconnectionEvent,
        );
      }
      if (targetId) {
        handleHighlighting(
          context.plugins.get('Annotations'),
          targetIdentifierPredicate,
          `[data-thread-id="${targetId}"]`,
        );
      }
    },
    [
      handleSessionDisconnectionEvent,
      setupCommentButton,
      setupComments,
      targetId,
      targetIdentifierPredicate,
      handleHighlighting,
      commentsPermission,
      currentUser,
    ],
  );

  const contextConfig = useMemo(
    () =>
      getIndependentContextConfig({
        fileDocument: qualioDocument,
        collaborationToken,
        commentsPermission,
        sidebarConfig: { sidebarElement, preventScrollOutOfView: true },
        users,
        tags,
      }),
    [
      qualioDocument,
      collaborationToken,
      commentsPermission,
      sidebarElement,
      users,
      tags,
    ],
  );

  return (
    <>
      <CKEditorContext
        context={DecoupledEditor.Context}
        contextWatchdog={DecoupledEditor.ContextWatchdog}
        isLayoutReady={isSidebarRendered}
        config={contextConfig}
        onReady={(context: Context) => contextReadyHandler(context)}
      />
      <QBox
        id="commentsSidebar"
        data-cy="fileDocumentCommentsSidebar"
        my={1}
        ml={1}
      />
    </>
  );
};
